import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import {
  LOGO_ONLY_LAYOUT,
  TEXT_AND_LOGO_LAYOUT,
  TEXT_ONLY_LAYOUT
} from 'reservations/account/constants';
import { BENTOBOX_ACCOUNT_ENDPOINT } from 'reservations/base/constants/endpoints';
import { DEFAULT_QUERY_OPTIONS } from 'reservations/base/constants/queryOptions';
import { REDIRECT_BASE_404 } from 'reservations/base/constants/redirects';
import { buildBentoboxApiEndpoint } from 'reservations/base/helpers/buildBentoboxApiEndpoint';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import { Brand, BrandImage } from 'reservations/brand/types/brand';
import { DEFAULT_WAITLIST_CLOSED_TEXT } from 'reservations/waitlist/constants';
import type { RequestError } from 'utility/restClient';
import { restClient } from 'utility/restClient';
import { defaultWhenNullish } from 'utility/zod';

const Logo = z.object({
  url: z.string(),
  alt_text: z.string()
});
type Logo = z.infer<typeof Logo>;

const GoogleMapsStaticUrl = z.object({
  static_url_mobile: z.string(),
  static_url_desktop: z.string(),
  static_url_tablet: z.string(),
  lat: z.number(),
  lng: z.number()
});
type GoogleMapsStaticUrl = z.infer<typeof GoogleMapsStaticUrl>;

const BoxLocationWithMap = z.object({
  id: z.string().or(z.number()),
  lat: z.string().nullish(),
  lng: z.string().nullish(),
  street: z.string(),
  state: z.string(),
  postal_code: z.string(),
  city: z.string(),
  phone_number: defaultWhenNullish(z.string(), ''),
  email_address: defaultWhenNullish(z.string(), ''),
  name: z.string().default(''),
  slug: z.string(),
  google_maps_static_urls: GoogleMapsStaticUrl,
  fb_location_pixel_id: z.string().nullish(),
  order: z.number()
});
type BoxLocationWithMap = z.infer<typeof BoxLocationWithMap>;

const LocationIntegration = z.object({
  location: BoxLocationWithMap,
  restaurant_id: z.string(),
  waitlist_closed_text: z.string().default(DEFAULT_WAITLIST_CLOSED_TEXT)
});
export type LocationIntegration = z.infer<typeof LocationIntegration>;

const PaymentProviderInformation = z.object({
  payment_provider: defaultWhenNullish(z.string(), ''),
  is_test_mode: defaultWhenNullish(z.boolean(), true),
  stripe_publishable_key: defaultWhenNullish(z.string(), ''),
  stripe_user_id: defaultWhenNullish(z.string(), ''),
  bentobox_stripe_publishable_key: defaultWhenNullish(z.string(), ''),
  square_application_id: defaultWhenNullish(z.string(), ''),
  square_location_id: defaultWhenNullish(z.string(), ''),
  clover_payment_form_url: defaultWhenNullish(z.string(), ''),
  clover_platform_public_key: defaultWhenNullish(z.string(), ''),
  clover_credit_card_surcharge_rate: defaultWhenNullish(z.number(), 0)
});

export type PaymentProviderInformation = z.infer<
  typeof PaymentProviderInformation
>;

const AccountResponse = z.object({
  connected_location_integrations: z.array(LocationIntegration),
  dev_mode: z.boolean(),
  logo: z.array(Logo),
  primary_color: z.string(),
  restaurant_name: z.string(),
  logo_destination_url: z.string().default('/make-a-reservation'),
  bento_analytics_script_src: z.string().default(''),
  indicative_api_key: z.string().default(''),
  snowplow_collector_url: z.string().default(''),
  has_bentobox_reservations_card_on_file_feature: z.boolean().default(false),
  has_brand_engine_feature: z.boolean().default(false),
  has_pre_dining_experience_feature: z.boolean().default(false),
  payment_provider_information: PaymentProviderInformation.nullish(),
  favicon_url: z.string().nullable(),
  brand: Brand,
  brand_image: z.array(BrandImage).nullish(),
  header_layout: z
    .enum([TEXT_ONLY_LAYOUT, LOGO_ONLY_LAYOUT, TEXT_AND_LOGO_LAYOUT])
    .default(TEXT_ONLY_LAYOUT),
  has_clover_opt_in_messages_feature: z.boolean().default(false)
});

type AccountResponse = z.infer<typeof AccountResponse>;

const AccountResponseWithoutLogoAndBrandImage = AccountResponse.omit({
  logo: true,
  brand_image: true
});
type AccountResponseWithoutLogoAndBrandImage = z.infer<
  typeof AccountResponseWithoutLogoAndBrandImage
>;

interface SelectAccountResponse
  extends AccountResponseWithoutLogoAndBrandImage {
  logoDestinationUrl: string;
  locationIntegrationBySlug: Record<string, LocationIntegration>;
  hasImage: boolean;
  logo: Logo;
  restaurantName: string;
  brandImage: BrandImage;
  brandLogo: Logo;
  hasBrandImage: boolean;
  headerLayout: AccountResponse['header_layout'];
}

async function fetchAccount() {
  const endpoint = buildBentoboxApiEndpoint(BENTOBOX_ACCOUNT_ENDPOINT);
  const response = await restClient.get(endpoint);

  try {
    return AccountResponse.parse(response);
  } catch (err) {
    /* istanbul ignore next */
    throw err;
  }
}

export function useAccountQuery() {
  const { handleNavigation } = useCustomRouter();

  return useQuery<AccountResponse, RequestError, SelectAccountResponse>({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['account'],
    queryFn: fetchAccount,
    select: data => ({
      ...data,
      restaurantName: data.restaurant_name,
      logoDestinationUrl: data.logo_destination_url,
      hasImage: !!data.logo.length,
      logo: data.logo?.[0] ?? { url: '', alt_text: '' },
      locationIntegrationBySlug: data.connected_location_integrations.reduce(
        (accum, locationIntegration) => {
          accum[locationIntegration.location.slug] = locationIntegration;
          return accum;
        },
        {} as Record<string, LocationIntegration>
      ),
      // istanbul ignore next
      brandImage: data.brand_image?.[0] ?? {
        url: '',
        alt_text: '',
        image_large_url: ''
      },
      brandLogo: data.brand.logos?.[0] ?? { url: '', alt_text: '' },
      hasBrandImage: !!data.brand_image?.length,
      headerLayout: data.header_layout
    }),
    meta: {
      onQueryError: () => {
        // istanbul ignore next
        handleNavigation(REDIRECT_BASE_404);
      }
    }
  });
}

export type {
  BoxLocationWithMap,
  AccountResponse,
  SelectAccountResponse,
  Logo,
  LocationIntegration
};
